import React, { useEffect, useState, useContext, useCallback, useRef } from 'react';
import axios from '../api/axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/authcontext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowRight, faXmark, faCalendarDays, faStethoscope,
  faClock, faLayerGroup, faCircleCheck, faUserTie,
  faPenNib, faMagnifyingGlass, faLanguage
} from '@fortawesome/free-solid-svg-icons';
import '../style.css';

// Bare origin, used for /uploads/<image>. Not the same as REACT_APP_API_URL,
// which ends in /api and is used by the axios instance.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3000';

/* ─────────────────────────────────────────
   CONSTANTS & FILTERS
   Canonical 14-category list — must match the `specialisations` table seeded
   by migration-specialisations.sql. The old 6-pill list didn't match any real
   category, so filtering never worked once specialisations were populated.
───────────────────────────────────────── */
// The filter tiles are no longer a hardcoded list. They are built from the
// specialisations the listed experts actually have (see `currentFilters`
// below), so a tile can never exist that matches nobody, and a specialisation
// that is spelled differently in the database still gets its own working tile.

const CARD_GRADIENTS = [
  ['#c5cae9', '#7986cb'], ['#b2dfdb', '#4db6ac'], ['#f8bbd0', '#f06292'],
  ['#ffe0b2', '#ef9800'], ['#d1c4e9', '#9575cd'], ['#b2ebf2', '#4dd0e1'],
];

function getInitials(name = '') {
  return name.split(' ')
    .filter((_, i) => i > 0 || !name.toLowerCase().startsWith('dr'))
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('');
}

// Endpoints may return a bare array or { success, data: [...] } depending on
// which controller generation they come from. Normalise both.
function extractList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
}

// Therapists now carry `specialisations` as an array of strings from the
// backend (docinfo.specialisation is a single legacy column, unused here).
// Graphologists still use a single `specialisation` string — untouched.
function getSpecialisationList(expert, isTherapist) {
  if (isTherapist) return Array.isArray(expert.specialisations) ? expert.specialisations : [];
  return expert.specialisation ? [expert.specialisation] : [];
}

/* ─────────────────────────────────────────
   COMPONENT
───────────────────────────────────────── */
const Doctors = () => {
  // Data states
  const [therapists, setTherapists] = useState([]);
  const [graphologists, setGraphologists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI States
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialTab = queryParams.get('tab') === 'graphologists' ? 'graphologists' : 'therapists';
  // The homepage's quick-match dropdown, and now the matching quiz, both link
  // here with ?focus=<category name>. This was previously read nowhere —
  // the query string arrived and was silently ignored, so "match me with a
  // therapist" always landed on the unfiltered list regardless of what was
  // picked.
  const initialFocus = queryParams.get('focus');

  const [activeTab, setActiveTab] = useState(initialTab);
  // Starts on the ?focus= value if there is one. Once the data loads, the
  // effect further down drops back to 'All' if no expert has that focus.
  const [activeFilter, setActiveFilter] = useState(initialFocus || 'All');
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 600);

  const { isAuthenticated, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();

  /* ── Fetch Data ── */
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [therapistRes, graphologistRes] = await Promise.allSettled([
          axios.get('/doctors'),
          axios.get('/graphologists')
        ]);

        if (therapistRes.status === 'fulfilled') {
          setTherapists(extractList(therapistRes.value.data));
        } else {
          setError('Failed to load experts data.');
        }

        if (graphologistRes.status === 'fulfilled') {
          setGraphologists(extractList(graphologistRes.value.data));
        }
      } catch (err) {
        setError('Failed to load experts data.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  /* ── Track viewport & Scroll Lock ── */
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (selectedExpert) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = 'unset';
    const handleEsc = (e) => { if (e.key === 'Escape') setSelectedExpert(null); };
    window.addEventListener('keydown', handleEsc);
    return () => { document.body.style.overflow = 'unset'; window.removeEventListener('keydown', handleEsc); };
  }, [selectedExpert]);

  /* ── Reset filter when tab changes ── */
  // Skips the very first run: without this, the effect fires on mount too
  // (it depends on activeTab, which always "changes" from undefined on
  // mount), immediately resetting the activeFilter we just initialized from
  // the focus query param back to 'All' before the page ever rendered it.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setActiveFilter('All');
  }, [activeTab]);

  /* ── Navigate to booking ── */
  const handleBook = useCallback((expert) => {
    if (authLoading) return;

    // Distinct routes, not a query param. The old
    // `/bookings/${id}?type=graphologist` matched the therapist route
    // `/bookings/:doctorId`, because the router ignores the query string.
    // Since users/docinfo/graphinfo have separate id spaces, that loaded the
    // THERAPIST with the same numeric id — the client saw the wrong person's
    // slots and could book a therapy session thinking it was an analysis.
    const route = activeTab === 'graphologists'
      ? `/booking/graphologist/${expert.id}`
      : `/bookings/${expert.id}`;

    if (!isAuthenticated) navigate('/login', { state: { referrer: route } });
    else navigate(route);
  }, [authLoading, isAuthenticated, navigate, activeTab]);

  /* ── Filtering Logic ── */
  const currentData = activeTab === 'therapists' ? therapists : graphologists;
  const isTherapistTab = activeTab === 'therapists';

  // Tiles come from the data: every distinct specialisation on the current
  // tab, most common first, each with how many experts have it.
  const filterCounts = {};
  currentData.forEach((e) => {
    getSpecialisationList(e, isTherapistTab).forEach((s) => {
      filterCounts[s] = (filterCounts[s] || 0) + 1;
    });
  });
  const currentFilters = [
    'All',
    ...Object.keys(filterCounts).sort((a, b) => filterCounts[b] - filterCounts[a] || a.localeCompare(b)),
  ];

  // If the selected tile doesn't exist once data is in (e.g. a ?focus= link
  // for a specialisation nobody has), fall back to showing everyone rather
  // than an empty page.
  useEffect(() => {
    if (!loading && activeFilter !== 'All' && !filterCounts[activeFilter]) {
      setActiveFilter('All');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, therapists, graphologists, activeTab]);

  const filteredExperts = activeFilter === 'All'
    ? currentData
    : currentData.filter((e) => getSpecialisationList(e, isTherapistTab).includes(activeFilter));

  /* ─── Loading / Error States ─── */
  if (loading) {
    return (
      <div className="doctors-status-wrap" style={{ minHeight: '50vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p>Finding experts for you…</p>
      </div>
    );
  }

  if (error && activeTab === 'therapists' && therapists.length === 0) {
    return (
      <div className="doctors-status-wrap" style={{ textAlign: 'center', padding: '40px' }}>
        <p style={{ color: 'red' }}>{error}</p>
      </div>
    );
  }

  const drawerClass = ['doctor-drawer', isMobile ? 'drawer--bottom' : 'drawer--right', selectedExpert ? 'drawer--open' : ''].filter(Boolean).join(' ');

  return (
    <>
      {/* The card's "Profile →" link was nearly invisible in style.css. These
          rules live here because that stylesheet is separate; !important is
          needed to win over the existing .view-profile-btn rule. */}
      <style>{`
        .pro .view-profile-btn {
          display: inline-flex !important;
          align-items: center;
          padding: 7px 14px !important;
          border-radius: 50px !important;
          border: 1.5px solid #1a2344 !important;
          background: rgba(26, 35, 68, 0.06) !important;
          color: #1a2344 !important;
          font-weight: 700 !important;
          font-size: 12px !important;
          opacity: 1 !important;
          transition: background 0.2s ease, color 0.2s ease, transform 0.2s ease;
        }
        .pro .view-profile-btn svg { transition: transform 0.2s ease; }
        .pro:hover .view-profile-btn,
        .pro .view-profile-btn:hover {
          background: #1a2344 !important;
          color: #ffffff !important;
        }
        .pro:hover .view-profile-btn svg { transform: translateX(3px); }

        /* Same-size photos: every card gets an identical square frame and the
           image is cropped to fill it, instead of keeping its own proportions
           (which left landscape photos shorter, with empty space above). The
           crop is anchored a little above centre so faces aren't cut off. */
        .pro .pro-img-wrap {
          width: 100% !important;
          aspect-ratio: 1 / 1 !important;
          height: auto !important;
          overflow: hidden !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        .pro .pro-img-wrap img {
          width: 100% !important;
          height: 100% !important;
          max-height: none !important;
          object-fit: cover !important;
          object-position: center 20% !important;
        }
      `}</style>

      <section id="doc1" className="section-p1 doctors-page">
        <div className="doctors-header" style={{ textAlign: 'center', marginBottom: '40px' }}>
          <span className="eyebrow">Our Directory</span>
          <h2>Find Your Expert</h2>
          <p className="featpara">Whether you need a compassionate ear or deep self-discovery, we are here.</p>

          {/* THE TOGGLE SWITCH */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px' }}>
            <div style={{ display: 'inline-flex', background: 'rgba(48,60,108,0.08)', borderRadius: '50px', padding: '6px', position: 'relative' }}>
              <button
                onClick={() => setActiveTab('therapists')}
                style={{
                  position: 'relative', zIndex: 2, padding: '12px 24px', borderRadius: '50px',
                  border: 'none', background: activeTab === 'therapists' ? 'var(--primary-dark)' : 'transparent',
                  color: activeTab === 'therapists' ? 'white' : 'var(--text-secondary)',
                  fontWeight: 600, fontSize: '15px', cursor: 'pointer', transition: 'all 0.3s ease'
                }}
              >
                🛋️ Talk Therapy
              </button>
              <button
                onClick={() => setActiveTab('graphologists')}
                style={{
                  position: 'relative', zIndex: 2, padding: '12px 24px', borderRadius: '50px',
                  border: 'none', background: activeTab === 'graphologists' ? 'var(--primary-dark)' : 'transparent',
                  color: activeTab === 'graphologists' ? 'white' : 'var(--text-secondary)',
                  fontWeight: 600, fontSize: '15px', cursor: 'pointer', transition: 'all 0.3s ease'
                }}
              >
                ✍️ Handwriting Analysis
              </button>
            </div>
          </div>
        </div>

        <div className="doc-filter-row">
          {currentFilters.map((pill) => (
            <button
              key={pill}
              className={`doc-filter-pill${activeFilter === pill ? ' active' : ''}`}
              onClick={() => setActiveFilter(pill)}
            >
              {pill}
              <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.6, fontWeight: 600 }}>
                {pill === 'All' ? currentData.length : filterCounts[pill]}
              </span>
            </button>
          ))}
        </div>

        <div className="pro-container">
          {filteredExperts.length > 0 ? (
            filteredExperts.map((expert, i) => {
              const [c1, c2] = CARD_GRADIENTS[i % CARD_GRADIENTS.length];
              const expertBio = expert.bio || expert.description || 'Dedicated to your personal growth and wellbeing.';
              const expertFee = expert.fee || `₹ ${expert.price || (activeTab === 'therapists' ? '250' : '500')}`;
              const specList = getSpecialisationList(expert, isTherapistTab);

              return (
                <div className="pro" key={expert.id} onClick={() => setSelectedExpert(expert)}>
                  <div
                    className="pro-img-wrap"
                    style={{
                      background: `linear-gradient(145deg, ${c1}, ${c2})`,
                      width: '100%',
                      height: 'auto',
                      aspectRatio: '1 / 1',
                      overflow: 'hidden',
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {expert.image ? (
                      <img
                        src={`${BACKEND_URL}/uploads/${expert.image}`}
                        alt={expert.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          maxHeight: 'none',
                          objectFit: 'cover',
                          objectPosition: 'center 20%',
                          display: 'block'
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="pro-img-initials" style={{ color: c2 }}>{getInitials(expert.name)}</span>
                    )}
                    <div className="pro-img-overlay"><span className="pro-overlay-btn">View profile</span></div>
                  </div>

                  <div className="des">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
                      {specList.length > 0 ? (
                        <>
                          {specList.slice(0, 2).map((s) => (
                            <span
                              key={s}
                              className="doc-specialty"
                              style={{ fontSize: 10, padding: '4px 10px', fontWeight: 600 }}
                            >
                              {s}
                            </span>
                          ))}
                          {specList.length > 2 && (
                            <span
                              className="doc-specialty"
                              style={{
                                fontSize: 10, padding: '4px 10px', fontWeight: 600,
                                background: 'rgba(48,60,108,0.05)', color: 'var(--text-light, #9ca3af)'
                              }}
                            >
                              +{specList.length - 2}
                            </span>
                          )}
                        </>
                      ) : (
                        <span className="doc-specialty" style={{ fontSize: 10, padding: '4px 10px', fontWeight: 600 }}>
                          {isTherapistTab ? 'General Therapy' : 'Graphologist'}
                        </span>
                      )}
                    </div>
                    <h5 className="docname">{expert.name}</h5>
                    {Array.isArray(expert.languages) && expert.languages.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, margin: '0 0 8px' }}>
                        {expert.languages.slice(0, 3).map((lang) => (
                          <span
                            key={lang}
                            style={{
                              fontSize: 10, fontWeight: 600, padding: '4px 10px', borderRadius: 50,
                              background: 'var(--sage-lite, #e8f3ee)', color: 'var(--accent-sage, #3f7d62)'
                            }}
                          >
                            {lang}
                          </span>
                        ))}
                        {expert.languages.length > 3 && (
                          <span
                            style={{
                              fontSize: 10, fontWeight: 600, padding: '4px 10px', borderRadius: 50,
                              background: 'rgba(48,60,108,0.05)', color: '#9ca3af'
                            }}
                          >
                            +{expert.languages.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                    <p className="para">{expertBio.substring(0, 60)}...</p>
                    <div className="pro-card-footer">
                      <span className="doc-fee">{expertFee}</span>
                      <span className="view-profile-btn">Profile <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: 10, marginLeft: '4px' }} /></span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="no-doctors-msg" style={{ width: '100%', textAlign: 'center', padding: '40px', color: '#666' }}>
              <FontAwesomeIcon icon={activeTab === 'therapists' ? faUserTie : faMagnifyingGlass} style={{ fontSize: '24px', marginBottom: '10px', display: 'block', margin: '0 auto' }} />
              No {activeTab} found for this filter.
            </p>
          )}
        </div>
      </section>

      {/* OVERLAY & DRAWER */}
      {selectedExpert && <div className="drawer-overlay drawer-overlay--open" onClick={() => setSelectedExpert(null)} />}

      <aside className={drawerClass}>
        <div className="drawer-drag-handle" />
        <button className="drawer-close-btn" onClick={() => setSelectedExpert(null)}><FontAwesomeIcon icon={faXmark} /></button>

        {selectedExpert && (
          <>
            <div className="drawer-hero">
              <div className="drawer-avatar-wrap">
                {selectedExpert.image ? (
                  <img src={`${BACKEND_URL}/uploads/${selectedExpert.image}`} alt={selectedExpert.name} className="drawer-avatar-img" />
                ) : (
                  <span className="drawer-avatar-initials">{getInitials(selectedExpert.name)}</span>
                )}
              </div>
              <h3 className="drawer-name">{selectedExpert.name}</h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, justifyContent: 'center' }}>
                {(() => {
                  const specList = getSpecialisationList(selectedExpert, isTherapistTab);
                  return specList.length > 0 ? (
                    specList.map((s) => (
                      <span key={s} className="drawer-spec-tag">{s}</span>
                    ))
                  ) : (
                    <span className="drawer-spec-tag">
                      {isTherapistTab ? 'Therapist' : 'Graphologist'}
                    </span>
                  );
                })()}
              </div>
            </div>

            <div className="drawer-scroll-body">
              <div className="drawer-section">
                <span className="drawer-label">
                  <FontAwesomeIcon icon={activeTab === 'therapists' ? faStethoscope : faPenNib} className="drawer-label-icon" /> About
                </span>
                <p className="drawer-bio">{selectedExpert.bio || selectedExpert.description || 'No description available.'}</p>
              </div>

              <div className="drawer-section">
                <span className="drawer-label"><FontAwesomeIcon icon={faLayerGroup} className="drawer-label-icon" /> At a glance</span>
                <div className="drawer-stats-grid">
                  <div className="stat-box">
                    <FontAwesomeIcon icon={faClock} className="stat-icon" />
                    <span className="stat-val">{selectedExpert.experience || '—'}</span>
                    <span className="stat-lbl">Experience</span>
                  </div>
                  <div className="stat-box">
                    <FontAwesomeIcon icon={faCircleCheck} className="stat-icon" />
                    <span className="stat-val">{selectedExpert.sessions || '0'}</span>
                    <span className="stat-lbl">{activeTab === 'therapists' ? 'Sessions' : 'Analyses'}</span>
                  </div>
                  <div className="stat-box">
                    <FontAwesomeIcon icon={faCalendarDays} className="stat-icon" />
                    <span className="stat-val">{selectedExpert.fee || `₹${selectedExpert.price || (activeTab === 'therapists' ? '250' : '500')}`}</span>
                    <span className="stat-lbl">{activeTab === 'therapists' ? 'Per session' : 'Per report'}</span>
                  </div>
                </div>
              </div>

              {selectedExpert.tags && selectedExpert.tags.length > 0 && (
                <div className="drawer-section">
                  <span className="drawer-label">Specialises in</span>
                  <div className="drawer-tag-row">
                    {selectedExpert.tags.map((tag) => (
                      <span key={tag} className="drawer-tag">{tag}</span>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(selectedExpert.languages) && selectedExpert.languages.length > 0 && (
                <div className="drawer-section">
                  <span className="drawer-label"><FontAwesomeIcon icon={faLanguage} className="drawer-label-icon" /> Speaks</span>
                  <div className="drawer-tag-row">
                    {selectedExpert.languages.map((lang) => (
                      <span key={lang} className="drawer-tag">{lang}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="drawer-footer">
              <button className="drawer-book-btn" onClick={() => handleBook(selectedExpert)}>
                <FontAwesomeIcon icon={activeTab === 'therapists' ? faCalendarDays : faPenNib} style={{ marginRight: '8px' }} />
                {activeTab === 'therapists' ? 'Schedule a session' : 'Request an Analysis'}
              </button>
            </div>
          </>
        )}
      </aside>
    </>
  );
};

export default Doctors;